import React, { useState } from 'react';
import { Form, InputGroup, Button, Dropdown } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';
import { useMessaging } from '../../../features/messaging/MessagingContext';
import Convo from './Convo';
import EmojiPicker from 'emoji-picker-react';
import moment from 'moment';

const ChatWindow = () => {
    const { state, dispatch } = useMessaging();
    const { selectedContact, messages } = state;
    const history = useHistory();
    const [newMessage, setNewMessage] = useState('');
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [isSending, setIsSending] = useState(false);

    const chatMessages = selectedContact ? (messages[selectedContact.id] || []) : [];
    const isPast24Hours = selectedContact?.lastInteraction
        ? moment().diff(moment(selectedContact.lastInteraction), 'hours') >= 24
        : false;

    const handleSendMessage = (e) => {
        e.preventDefault(); if (!newMessage.trim() || isSending) return;
        const msg = { id: Date.now().toString(), sender: 'me', content: newMessage, time: moment().format('hh:mm A'), status: 'sending', timestamp: new Date() };
        dispatch({ type: 'ADD_MESSAGE', payload: { contactId: selectedContact.id, message: msg } });
        setNewMessage(''); setIsSending(true);
        setTimeout(() => setIsSending(false), 1000);
    };

    if (!selectedContact) {
        return (
            <div className="h-100 d-flex flex-column align-items-center justify-content-center bg-white shadow-inner-sm text-center p-5">
                <i className="fab fa-whatsapp fa-6x text-navy-blue opacity-10 mb-4" />
                <h4 className="font-weight-bold text-navy-blue">Ready to Chat?</h4>
                <p className="text-muted small max-width-300">Select a contact to start an end-to-end encrypted conversation.</p>
            </div>
        );
    }

    return (
        <div className="d-flex flex-column h-100 bg-chat-pattern relative shadow-luxury">
            <div className="chat-header p-3 bg-white d-flex align-items-center justify-content-between shadow-sm z-index-10 border-bottom border-indigo-50">
                <div className="d-flex align-items-center">
                    <div className="avatar-pill rounded-circle avatar-navy d-flex align-items-center justify-content-center mr-3 shadow-sm text-white">
                        {selectedContact.name.charAt(0)}
                    </div>
                    <div>
                        <h6 className="mb-0 font-weight-bold text-navy-blue">{selectedContact.name}</h6>
                        <small className="text-success small font-weight-bold opacity-75">
                            <i className="fas fa-circle mr-1" style={{ fontSize: '8px' }} /> Online
                        </small>
                    </div>
                </div>
                <div className="d-flex align-items-center">
                    <Button variant="link" className="text-navy-blue mr-2 p-2 hover-bg-slate"><i className="fas fa-search" /></Button>
                    <Dropdown drop="left">
                        <Dropdown.Toggle as={Button} variant="link" className="text-navy-blue p-2 hover-bg-slate no-caret transition-all">
                            <i className="fas fa-ellipsis-v" />
                        </Dropdown.Toggle>
                        <Dropdown.Menu className="shadow-lg border-0 rounded-lg p-2 mt-2">
                            <Dropdown.Item className="rounded py-2"><i className="fas fa-user-shield mr-3 opacity-50" /> Verify Security</Dropdown.Item>
                            <Dropdown.Item className="rounded py-2"><i className="fas fa-ban mr-3 opacity-50" /> Block Contact</Dropdown.Item>
                            <Dropdown.Divider />
                            <Dropdown.Item className="rounded py-2 text-danger"><i className="fas fa-trash-alt mr-3" /> Clear Conversation</Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>
                </div>
            </div>

            <div className="chat-body flex-grow-1 overflow-auto p-4 d-flex flex-column-reverse scrollbar-slate">
                <Convo messages={chatMessages} user={selectedContact} />
            </div>

            <div className="chat-footer p-3 bg-white border-top shadow-top">
                {isPast24Hours ? (
                    <div className="p-3 bg-slate-50 rounded-lg border border-warning-subtle text-center">
                        <p className="mb-2 small font-weight-bold text-navy-blue opacity-50">
                            24-Hour window expired. High-level template required.
                        </p>
                        <Button
                            variant="primary"
                            size="sm"
                            className="btn-navy-blue px-4 rounded-pill font-weight-bold"
                            onClick={() => history.push('/whatsapp-campaigns')}
                        >
                            SEND FORM TEMPLATE
                        </Button>
                    </div>
                ) : (
                    <Form onSubmit={handleSendMessage} className="relative">
                        <div className="d-flex align-items-center p-1 bg-slate-50 rounded-pill border-indigo-50 shadow-inner">
                            <Button
                                variant="link"
                                className={`text-navy-blue opacity-80 p-2 transition-all mr-1 ${showEmojiPicker ? 'text-primary' : ''}`}
                                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                            >
                                <i className="far fa-smile fa-lg" />
                            </Button>

                            <Dropdown drop="up" className="mr-1">
                                <Dropdown.Toggle as={Button} variant="link" className="text-navy-blue opacity-80 p-2 rounded-circle no-caret transition-all">
                                    <i className="fas fa-plus fa-lg" />
                                </Dropdown.Toggle>
                                <Dropdown.Menu className="shadow-lg border-0 rounded-xl p-2 mb-3">
                                    <Dropdown.Item className="rounded py-2 px-3"><i className="fas fa-paperclip mr-3 text-primary" /> Document</Dropdown.Item>
                                    <Dropdown.Item className="rounded py-2 px-3"><i className="fas fa-image mr-3 text-success" /> Gallery</Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown>

                            <Form.Control
                                placeholder="Write your message..."
                                className="border-0 bg-transparent py-2 shadow-none flex-grow-1"
                                value={newMessage}
                                onChange={(e) => setNewMessage(e.target.value)}
                            />

                            <Button
                                type="submit"
                                className="btn-navy-blue rounded-circle d-flex align-items-center justify-content-center ml-2 border-0"
                                style={{ width: '42px', height: '42px' }}
                                disabled={!newMessage.trim() || isSending}
                            >
                                {isSending ? <span className="spinner-border spinner-border-sm" /> : <i className="fas fa-paper-plane" />}
                            </Button>
                        </div>

                        {showEmojiPicker && (
                            <div className="emoji-picker-container absolute bottom-100 left-0 mb-3 slide-up">
                                <EmojiPicker onEmojiClick={(emoji) => setNewMessage(prev => prev + emoji.emoji)} width={300} height={400} />
                            </div>
                        )}
                    </Form>
                )}
            </div>
        </div>
    );
};

export default ChatWindow;
